import { useState, useCallback, useMemo } from 'react';
import { useAuth } from '../../../utils/Auth';
import { saveTagData } from '../../../utils/QRFunctions/qrActions';
import { characterLengthCheck } from '../../../lib/characterLengthCheck';
import styles from './Tags.module.css';

export default function Tags({ reference }) {
    const { qr_data, dataDispatch } = useAuth();
    const [tag, setTag] = useState('');

    const MAX_TAG_LENGTH = 20;

    const currentTags = useMemo(() => qr_data?.data?.tags || [], [qr_data?.data?.tags]);

    const handleAddTag = useCallback(async () => {
        const trimmedTag = tag.trim();
        if (!characterLengthCheck(trimmedTag, MAX_TAG_LENGTH)) {
            return;
        }

        if (trimmedTag && !currentTags.includes(trimmedTag)) {
            const updatedTags = [...currentTags, trimmedTag];
            dataDispatch({ type: 'tags', data: updatedTags });
            if (reference !== 'new') {
                await saveTagData(qr_data?.id, updatedTags)();
            }
        }
        setTag('');
    }, [tag, currentTags, dataDispatch, qr_data, reference]);

    const handleRemoveTag = useCallback(async (tagToRemove) => {
        const updatedTags = currentTags.filter((t) => t !== tagToRemove);
        dataDispatch({ type: 'tags', data: updatedTags });
        if (reference !== 'new') {
            await saveTagData(qr_data?.id, updatedTags)();
        }
    }, [currentTags, dataDispatch, qr_data, reference]);

    return (
        <div className={styles.container}>
            <div className={styles.panelLayout}>
                {/* Left: Tags List */}
                <div className={styles.tagList}>
                    <h4 className={styles.listTitle}>Tags</h4>
                    <div className={styles.scrollableList}>
                        <div className={styles.tags}>
                            {currentTags.length > 0 ? (
                                currentTags.map((t) => (
                                    <div key={t} className={styles.tag}>
                                        {t}
                                        <i
                                            className='fa-solid fa-xmark fa-fw'
                                            onClick={() => handleRemoveTag(t)}
                                            role="button"
                                            aria-label={`Remove tag ${t}`}
                                            tabIndex="0"
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    handleRemoveTag(t);
                                                }
                                            }}
                                        />
                                    </div>
                                ))
                            ) : (
                                <p className={styles.noTagsMessage}>No tags added yet.</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right: Tag Actions */}
                <div className={styles.actionsPanel}>
                    <h4 className={styles.panelTitle}>Tag Actions</h4>
                    <div className={styles.inputGroup}>
                        <input
                            type='text'
                            id='tag-name'
                            className={styles.input}
                            value={tag}
                            maxLength={MAX_TAG_LENGTH}
                            onChange={(e) => setTag(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddTag();
                                }
                            }}
                            placeholder="Tag name"
                        />
                    </div>
                    <div className={styles.actionButtons}>
                        <button
                            type="button"
                            className={styles.btnSecondary}
                            onClick={() => setTag('')}
                            disabled={!tag}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className={styles.btnPrimary}
                            onClick={handleAddTag}
                            disabled={!characterLengthCheck(tag, MAX_TAG_LENGTH, { trim: true })}
                        >
                            <i className="fa-regular fa-plus" />
                            Add
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}